import { useEffect, useMemo, useState } from 'react'
import { LoaderFunctionArgs, useLoaderData } from 'react-router-dom'
import { PaymentsIndex, fetchPayments } from '../../api/payments'
import { hcCurrency } from '../../lib/currency'
import { ButtonAnchor, DATA_TABLE_LOCALE, DataTable, PageHeading, Panel, TableColumn } from '../../components/common'
import { trackEvent } from '../../lib/config'

const PAGE_LENGTH = 20
const SEARCH_DELAY_MS = 750

export function loader({ params }: LoaderFunctionArgs): Promise<PaymentsIndex> {
  return fetchPayments(Number(params.healthCenterId))
}

export default function PaymentsIndexPage() {
  const data = useLoaderData() as Awaited<ReturnType<typeof loader>>
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)

  const matched = useMemo(() => {
    const term = search.trim().toLowerCase()
    const { payments } = data
    if (term.length === 0) {
      return payments
    }
    return payments.filter((payment) =>
      [payment.order_id, payment.state, payment.paid_at, hcCurrency(payment.amount), payment.begin_date, payment.end_date]
        .join(' ')
        .toLowerCase()
        .includes(term),
    )
  }, [data, search])

  useEffect(() => {
    if (search.length === 0) {
      return
    }
    // The filter applies on every keystroke, so one event per pause in typing stands in
    // for the submit the page does not have.
    const timer = window.setTimeout(() => {
      trackEvent('Payment History Searched', { has_query: true, query_length: search.length, result_count: matched.length })
    }, SEARCH_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [search])

  const pageCount = Math.max(1, Math.ceil(matched.length / PAGE_LENGTH))
  const current = Math.min(page, pageCount - 1)
  const rows = matched.slice(current * PAGE_LENGTH, (current + 1) * PAGE_LENGTH)
  const from = matched.length === 0 ? 0 : current * PAGE_LENGTH + 1

  const columns: readonly TableColumn<PaymentsIndex['payments'][number]>[] = [
    {
      key: 'order_id',
      label: 'ID',
      cellClassName: 'default-text-color',
      render: (payment) => <a href={payment.path}>{payment.order_id}</a>,
    },
    { key: 'state', label: 'Estado', cellClassName: 'default-text-color', render: (payment) => payment.state },
    { key: 'paid_at', label: 'Fecha de pago', cellClassName: 'default-text-color', render: (payment) => payment.paid_at },
    { key: 'amount', label: 'Monto', cellClassName: 'default-text-color', render: (payment) => hcCurrency(payment.amount) },
    { key: 'begin_date', label: 'Desde', cellClassName: 'default-text-color', render: (payment) => payment.begin_date },
    { key: 'end_date', label: 'Hasta', cellClassName: 'default-text-color', render: (payment) => payment.end_date },
  ]

  return (
    <div className="row" style={{ paddingTop: 10 }}>
      <div className="col-md-10 col-md-offset-1 col-xs-12">
        <Panel style={{ marginTop: 10 }}>
          <div className="well" style={{ border: 'none', boxShadow: '0 0px' }}>
            <div className="row">
              <PageHeading>Historial de pagos</PageHeading>
            </div>
            <div className="row">
              <div className="col-xs-12 col-md-10 col-md-offset-1">
                <DataTable
                  columns={columns}
                  rows={rows}
                  getRowKey={(payment) => payment.id}
                  tableClassName="patients-table"
                  search={search}
                  onSearchChange={(value) => {
                    setSearch(value)
                    setPage(0)
                  }}
                  searchInputStyle={{ display: 'inline-block', width: 'auto', fontSize: 18 }}
                  page={current}
                  pageCount={pageCount}
                  onPageChange={setPage}
                  pagination="simple"
                  total={matched.length}
                  from={from}
                  to={current * PAGE_LENGTH + rows.length}
                  emptyMessage={data.payments.length === 0 ? DATA_TABLE_LOCALE.empty : DATA_TABLE_LOCALE.noMatches}
                />
              </div>
              <div className="col-xs-12" style={{ textAlign: 'center' }}>
                <ButtonAnchor href={data.profile_path}>Ir a mi perfil</ButtonAnchor>
              </div>
            </div>
          </div>
        </Panel>
      </div>
    </div>
  )
}
